import { Suspense, useEffect, useMemo, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import URDFLoader from 'urdf-loader';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

import { getKioskRobotType } from '@/lib/kioskMode';
import { useTheme } from '@/hooks/useTheme';

// The robot_models URDFs are all-white by convention; the only color logic
// here is darkening the mesh in light mode so it reads against a light page.
const LIGHT_MODE_MESH_COLOR = 0x2f333c;

// public/urdf/<robot_type> symlinks the canonical robot_models/full_robots
// trees (openarm excluded — no openarm kiosks). Clients lazily fetch ONLY the
// model they render, so bundling several robots costs server disk, not client
// bandwidth. Every robot follows the standard layout: urdf/<type>.urdf with
// mesh refs as literal model-root-relative paths.
const URDF_PATHS: Record<string, string> = {
  yam: 'urdf/yam.urdf',
  yam_lift: 'urdf/yam_lift.urdf',
  big_yam: 'urdf/big_yam.urdf',
  fr3_duo: 'urdf/fr3_duo.urdf',
};

// Cache the parsed robot per robot_type at module scope so state-machine
// transitions that unmount and remount this component don't re-fetch and
// re-parse the URDF and all STL meshes.
const robotPromises = new Map<string, Promise<THREE.Object3D>>();

function loadRobot(robotType: string): Promise<THREE.Object3D> {
  const type = robotType in URDF_PATHS ? robotType : 'yam';
  const cached = robotPromises.get(type);
  if (cached) return cached;
  const promise = new Promise<THREE.Object3D>((resolve) => {
    // URDFLoader's callback fires after the XML parse, BEFORE the mesh files
    // finish loading — resolve only when the manager reports everything done,
    // so consumers (camera fit) never measure a geometry-less robot.
    const manager = new THREE.LoadingManager();
    let parsed: THREE.Object3D | null = null;
    let allLoaded = false;
    const maybeResolve = () => {
      if (parsed && allLoaded) resolve(parsed);
    };
    manager.onLoad = () => {
      allLoaded = true;
      maybeResolve();
    };
    const loader = new URDFLoader(manager);
    // Vite's BASE_URL is '/' for cloud deploy and '/kiosk/' for the local
    // touchscreen build, so URDF assets resolve correctly under either mount.
    const urdfBase = `${import.meta.env.BASE_URL}urdf/${type}`;
    // Mesh refs are LITERAL model-root-relative paths ("meshes/*.STL") by
    // repo convention, so the model root is the single resolution base.
    // The trailing slash is required — urdf-loader concatenates directly.
    loader.workingPath = `${urdfBase}/`;

    loader.loadMeshCb = (path, manager, done) => {
      new STLLoader(manager).load(
        path,
        (geometry) => {
          geometry.computeVertexNormals();
          done(new THREE.Mesh(geometry, new THREE.MeshStandardMaterial()));
        },
        undefined,
        (err) => done(new THREE.Object3D(), err as Error),
      );
    };

    loader.load(`${urdfBase}/${URDF_PATHS[type]}`, (robot) => {
      // ROS is Z-up; Three.js is Y-up
      robot.rotation.x = -Math.PI / 2;
      parsed = robot;
      maybeResolve();
    });
  });
  robotPromises.set(type, promise);
  return promise;
}

function RobotModel() {
  const [robot, setRobot] = useState<THREE.Object3D | null>(null);
  const { theme } = useTheme();
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as { target: THREE.Vector3; minDistance: number; maxDistance: number; update: () => void } | null;
  const invalidate = useThree((s) => s.invalidate);

  const material = useMemo(
    () => new THREE.MeshStandardMaterial({ roughness: 0.55, metalness: 0.25 }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    getKioskRobotType()
      .then((type) => loadRobot(type))
      .then((r) => {
        if (!cancelled) setRobot(r);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    material.color.set(theme === 'light' ? LIGHT_MODE_MESH_COLOR : 0xffffff);
    invalidate();
  }, [material, theme, invalidate]);

  // Flatten every link to the shared single-color material (urdf-loader
  // applies the URDF's per-link colors on top of loadMeshCb output), then fit
  // the camera so any robot — arm pair or full lift — fills the view.
  useEffect(() => {
    if (!robot || !controls) return;
    robot.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = material;
    });
    robot.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(robot);
    if (box.isEmpty()) return; // empty box -> NaN center -> invisible scene
    const center = box.getCenter(new THREE.Vector3());
    const radius = box.getSize(new THREE.Vector3()).length() / 2;
    const fov = ((camera as THREE.PerspectiveCamera).fov * Math.PI) / 180;
    const dist = (radius / Math.tan(fov / 2)) * 1.05;
    camera.position
      .set(1, 0.6, 1)
      .normalize()
      .multiplyScalar(dist)
      .add(center);
    camera.lookAt(center);
    controls.target.copy(center);
    controls.minDistance = radius * 0.5;
    controls.maxDistance = radius * 6;
    controls.update();
    invalidate();
  }, [robot, controls, material, camera, invalidate]);

  return robot ? <primitive object={robot} /> : null;
}

export function URDFViewer() {
  return (
    <div className="relative w-full h-full">
      <Canvas
        gl={{ alpha: true, antialias: false, powerPreference: 'low-power' }}
        camera={{ position: [1.8, 1.4, 1.8], fov: 45 }}
        style={{ background: 'transparent' }}
        dpr={1}
        frameloop="demand"
      >
        {/* No <Environment>: drei presets fetch an HDR from an external CDN at
            runtime, which a robot kiosk can't rely on (and a stalled fetch
            suspends the whole canvas). Plain lights only. */}
        <ambientLight intensity={0.9} />
        <hemisphereLight intensity={0.6} groundColor={0x444444} />
        <directionalLight position={[5, 10, 5]} intensity={1.6} />
        <directionalLight position={[-4, 6, -4]} intensity={0.6} />

        <Suspense fallback={null}>
          <RobotModel />
        </Suspense>

        <OrbitControls makeDefault enableDamping enablePan={false} dampingFactor={0.06} />
      </Canvas>
    </div>
  );
}
